import { Component } from '@theme/component';

/**
 * @typedef {Object} TextTruncateRefs
 * @property {HTMLElement} content - The text being clamped
 * @property {HTMLButtonElement} toggleButton - The control that expands and collapses it
 */

/**
 * Expands text that is clamped to a few lines on mobile.
 *
 * Both labels are rendered and swapped in CSS, so this carries nothing but two flags:
 * `data-expanded` for the clamp and the button's own `aria-expanded`, and `data-overflows`
 * for whether there is anything to reveal at all. Liquid cannot know how many lines the
 * text wraps to, so a short paragraph would otherwise offer a control that does nothing.
 *
 * @extends {Component<TextTruncateRefs>}
 */
class TextTruncate extends Component {
  requiredRefs = ['content', 'toggleButton'];

  #resizeObserver = new ResizeObserver(() => this.#measureOverflow());

  connectedCallback() {
    super.connectedCallback();
    this.#measureOverflow();
    this.#resizeObserver.observe(this.refs.content);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  toggle() {
    const expanded = this.dataset.expanded !== 'true';

    this.dataset.expanded = expanded ? 'true' : 'false';
    this.refs.toggleButton.setAttribute('aria-expanded', this.dataset.expanded);
  }

  /** Clamped text overflows its own box, so the scroll height answers this. */
  #measureOverflow() {
    // Expanded, nothing is clamped and every height matches, which would read as "fits".
    if (this.dataset.expanded === 'true') return;

    // A pixel of slack: a clamped box's scroll height rounds off the line height and can sit
    // a fraction above its own client height with nothing actually hidden.
    const { content } = this.refs;
    this.dataset.overflows = content.scrollHeight - content.clientHeight > 1 ? 'true' : 'false';
  }
}

if (!customElements.get('text-truncate')) {
  customElements.define('text-truncate', TextTruncate);
}
